import { useEffect, useState, lazy, Suspense } from 'react';
import { calculateLevel, type AuthNoticePayload } from '@big2/shared';
import { useGameState } from '../net/GameProvider.js';
import { useI18n } from '../i18n.js';
import { getUserAvatar } from '../utils/avatar.js';
import { applyPageSeo, type InfoPageType } from '../utils/seo.js';
import { AuthForms } from './profile/AuthForms.js';
import { BillingSection } from './profile/BillingSection.js';
import { AccountSettingsSection } from './profile/AccountSettingsSection.js';
import { LeaderboardSection } from './profile/LeaderboardSection.js';
import { FriendsSection } from './profile/FriendsSection.js';
import './profile/profilePage.css';
import './profile/playerProfileModal.css';

const AdminPanel = lazy(() => import('./AdminPanel.js').then((m) => ({ default: m.AdminPanel })));

function formatGameTypeName(gameType: string, isMn: boolean): string {
  switch (gameType) {
    case 'muushig':
      return isMn ? '♣ Муушиг' : '♣ Muushig';
    case 'twentyone':
      return isMn ? '♦ 21' : '♦ 21';
    case 'daaluu':
      return isMn ? '🂍 Даалуу' : '🂍 Daaluu';
    default:
      return isMn ? '♠ Арван гурав' : '♠ Big Two';
  }
}

function formatRelativeTime(iso: string, isMn: boolean): string {
  try {
    const diffMs = Date.now() - Date.parse(iso);
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return isMn ? 'Дөнгөж сая' : 'Just now';
    if (mins < 60) return isMn ? `${mins} минутын өмнө` : `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return isMn ? `${hours} цагийн өмнө` : `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return isMn ? `${days} өдрийн өмнө` : `${days}d ago`;
  } catch {
    return '';
  }
}

export interface ProfilePageProps {
  onBack: () => void;
  onOpenInfoPage?: (page: InfoPageType) => void;
  initialTab?: 'profile' | 'friends' | 'leaderboard' | 'settings';
  onTabChange?: (tab: 'profile' | 'friends' | 'leaderboard' | 'settings') => void;
  onOpenPayments?: () => void;
  onOpenAuth?: (view?: 'signin' | 'signup') => void;
}

function formatAuthNoticeMessage(notice: AuthNoticePayload, isMn: boolean): string {
  if (!isMn) return notice.message;
  switch (notice.kind) {
    case 'verification_sent':
      return `Баталгаажуулах имэйл болон код (${notice.email ?? ''}) хаяг руу илгээгдлээ.`;
    case 'email_verified':
      return notice.message.toLowerCase().includes('sign') || notice.message.toLowerCase().includes('нэвт')
        ? isMn ? 'Амжилттай нэвтэрлээ!' : 'Signed in successfully!'
        : isMn ? 'Имэйл хаяг амжилттай баталгаажлаа!' : 'Email address verified successfully!';
    case 'password_reset_sent':
      return 'Хэрэв энэ имэйл бүртгэлтэй бол нууц үг сэргээх холбоос болон код илгээгдсэн.';
    case 'password_reset_ready':
      return 'Баталгаажуулалт амжилттай. Шинэ нууц үгээ оруулна уу.';
    case 'password_updated':
      return 'Таны нууц үг амжилттай шинэчлэгдлээ.';
    case 'profile_updated':
      return 'Профайлын мэдээлэл амжилттай хадгалагдлаа.';
    case 'email_change_sent':
      return 'Профайл шинэчлэгдлээ. Шинэ имэйл хаягаа баталгаажуулна уу.';
    case 'account_deleted':
      return 'Таны бүртгэл бүрмөсөн устгагдлаа.';
    case 'payment_success':
      return '🎉 Төлбөр амжилттай баталгаажлаа! Таны VIP гишүүнчлэл идэвхжлээ.';
    case 'coupon_redeemed':
      return '🎉 Купон амжилттай идэвхжлээ! Таны VIP эрх сунгагдлаа.';
    default:
      return notice.message;
  }
}

export function ProfilePage({
  onBack,
  onOpenInfoPage,
  initialTab = 'profile',
  onTabChange,
  onOpenPayments,
  onOpenAuth,
}: ProfilePageProps) {
  const { state, actions } = useGameState();
  const { lang, t } = useI18n();
  const { profile } = state;
  const [showAdmin, setShowAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState<'profile' | 'friends' | 'leaderboard' | 'settings'>(initialTab);

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  const handleSelectTab = (tab: 'profile' | 'friends' | 'leaderboard' | 'settings') => {
    setActiveTab(tab);
    onTabChange?.(tab);
  };

  const isMn = lang === 'mn';

  // Apply page metadata & SEO
  useEffect(() => {
    if (activeTab === 'leaderboard') {
      applyPageSeo({
        title: isMn
          ? 'Шилдэг Тоглогчдын Чансаа (Top 10 Leaderboard) | arvangurav.com'
          : 'Top Players Leaderboard | arvangurav.com',
        description: isMn
          ? 'Монголын шилдэг онлайн Арван гурав болон Муушиг тоглогчдын албан ёсны чансаа, хожлын тоо болон түвшин.'
          : 'Official player rankings, win counts, and XP levels for Big Two and Muushig.',
        canonicalPath: '/leaderboard',
      });
    } else if (activeTab === 'friends') {
      applyPageSeo({
        title: isMn
          ? 'Найзуудын Жагсаалт ба Хүсэлт | arvangurav.com'
          : 'Friends & Social | arvangurav.com',
        description: isMn
          ? 'Найзуудаа нэмэх, онлайн байгаа эсэхийг нь харах, хамт тоглох.'
          : 'Add friends, see online status, and play games together.',
        canonicalPath: '/friends',
      });
    } else if (activeTab === 'settings') {
      applyPageSeo({
        title: isMn
          ? 'Тохиргоо ба VIP Гишүүнчлэл | arvangurav.com'
          : 'Account Settings & VIP Membership | arvangurav.com',
        description: isMn
          ? 'Хэрэглэгчийн тохиргоо, нууц үг солих, VIP гишүүнчлэл.'
          : 'User settings, password management, and VIP membership.',
        canonicalPath: '/settings',
      });
    } else {
      applyPageSeo({
        title: isMn
          ? 'Хэрэглэгчийн Профайл ба Түвшин | arvangurav.com'
          : 'User Profile & Level Progression | arvangurav.com',
        description: isMn
          ? 'Тоглогчийн профайл, XP түвшин, хожлын статистик болон Цай урамшуулал.'
          : 'Player profile, XP level progression, game stats, and Tsai token rewards.',
        canonicalPath: '/profile',
      });
    }
  }, [activeTab, isMn]);

  // Auto-fetch profile match history for signed-in user
  useEffect(() => {
    if (activeTab === 'profile' && profile?.id) {
      actions.inspectPlayer(profile.id);
    }
  }, [activeTab, profile?.id]);

  // Auto-poll payment status every 3.5s while QPay invoice modal is active
  useEffect(() => {
    if (!state.paymentInfo?.invoiceId) return;
    const invId = state.paymentInfo.invoiceId;
    const interval = setInterval(() => {
      actions.checkPayment(invId, { silent: true });
    }, 3500);
    return () => clearInterval(interval);
  }, [state.paymentInfo?.invoiceId]);

  const isPaidTier = profile?.tier === 'paid';
  const weeklyAmount = profile?.weeklyRewardAmount ?? (isPaidTier ? 160 : 40);
  const levelInfo = calculateLevel(profile?.xp ?? 0, isMn ? 'mn' : 'en');
  const winRate = profile && profile.gamesPlayed > 0
    ? Math.round((profile.wins / profile.gamesPlayed) * 100)
    : 0;

  const userRecentMatches =
    state.inspectedPlayer && state.inspectedPlayer.id === profile?.id
      ? state.inspectedPlayer.recentMatches
      : [];

  const lastClaimDate = profile?.lastWeeklyClaimAt
    ? new Date(profile.lastWeeklyClaimAt).toLocaleDateString(isMn ? 'mn-MN' : 'en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

  const nextWeeklyDate = profile?.nextWeeklyClaimAt
    ? new Date(profile.nextWeeklyClaimAt).toLocaleDateString(isMn ? 'mn-MN' : 'en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

  if (showAdmin && profile?.isAdmin) {
    return (
      <Suspense fallback={null}>
        <AdminPanel onClose={() => setShowAdmin(false)} />
      </Suspense>
    );
  }

  return (
    <section className="profile-page" aria-label={isMn ? 'Хэрэглэгчийн профайл' : 'User Profile'}>
      <div className="profile-page__inner">
        {/* Navigation Breadcrumb / Back button */}
        <nav className="profile-page__nav" aria-label={isMn ? 'Буцах зам' : 'Breadcrumb'}>
          <a
            href={state.roomId ? `/room/${state.roomId}` : '/'}
            onClick={(event) => {
              event.preventDefault();
              onBack();
            }}
          >
            <span aria-hidden="true">←</span>{' '}
            {state.roomId
              ? isMn
                ? 'Өрөө рүү буцах'
                : 'Back to room'
              : isMn
              ? 'Тоглоом руу буцах'
              : 'Back to game'}
          </a>
        </nav>

        {/* Page Header */}
        <header className="profile-page__header">
          <div>
            <p className="profile-page__eyebrow">
              {isMn ? 'БҮРТГЭЛ / ТОГЛОГЧИЙН ПРОФАЙЛ' : 'ACCOUNT / PLAYER PROFILE'}
            </p>
            <h1>{activeTab === 'leaderboard' ? (isMn ? 'Шилдэг тоглогчид' : 'Leaderboard') : t.profileTitle}</h1>
          </div>

          {profile?.isAdmin && (
            <button
              type="button"
              onClick={() => setShowAdmin(true)}
              style={{
                background: 'rgba(16, 185, 129, 0.25)',
                color: '#a7f3d0',
                border: '1px solid rgba(16, 185, 129, 0.45)',
                padding: '6px 14px',
                borderRadius: 8,
                fontWeight: 700,
              }}
            >
              ⚙️ {isMn ? 'Админ самбар' : 'Administration'}
            </button>
          )}
        </header>

        {/* Auth Notice Banner */}
        {state.authNotice && (
          <div
            role="status"
            style={{
              background: 'rgba(16, 185, 129, 0.18)',
              border: '1px solid rgba(16, 185, 129, 0.55)',
              color: '#a7f3d0',
              borderRadius: 10,
              padding: '10px 14px',
              fontSize: '0.9em',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <span>✓ {formatAuthNoticeMessage(state.authNotice, isMn)}</span>
            <button
              type="button"
              onClick={actions.clearAuthNotice}
              style={{
                background: 'transparent',
                color: '#a7f3d0',
                padding: '2px 8px',
                border: '1px solid rgba(167, 243, 208, 0.35)',
                fontSize: '0.85em',
                borderRadius: 4,
              }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Navigation Tabs (Profile vs Leaderboard vs Payments) */}
        <div role="tablist" className="profile-page__tabs">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'profile'}
            className={`profile-page__tab-btn ${activeTab === 'profile' ? 'is-active' : ''}`}
            onClick={() => handleSelectTab('profile')}
          >
            👤 {isMn ? 'Профайл' : 'Profile'}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'friends'}
            className={`profile-page__tab-btn ${activeTab === 'friends' ? 'is-active' : ''}`}
            onClick={() => handleSelectTab('friends')}
          >
            👥 {t.friendsTab}
            {state.friendsData?.incomingRequests && state.friendsData.incomingRequests.length > 0 && (
              <span
                style={{
                  background: '#eab308',
                  color: '#000',
                  borderRadius: 10,
                  padding: '1px 6px',
                  fontSize: '0.75rem',
                  fontWeight: 800,
                  marginLeft: 6,
                }}
              >
                {state.friendsData.incomingRequests.length}
              </span>
            )}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'leaderboard'}
            className={`profile-page__tab-btn ${activeTab === 'leaderboard' ? 'is-active' : ''}`}
            onClick={() => handleSelectTab('leaderboard')}
          >
            🏆 {isMn ? 'Чансаа (Top 10)' : 'Leaderboard'}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'settings'}
            className={`profile-page__tab-btn ${activeTab === 'settings' ? 'is-active' : ''}`}
            onClick={() => handleSelectTab('settings')}
          >
            ⚙️ {isMn ? 'Тохиргоо & VIP' : 'Settings & VIP'}
          </button>
          {onOpenPayments && (
            <button
              type="button"
              className="profile-page__tab-btn"
              onClick={() => onOpenPayments()}
            >
              💳 {isMn ? 'Төлбөрийн түүх' : 'Payment History'}
            </button>
          )}
        </div>

        {/* Main Content Card */}
        {activeTab === 'leaderboard' ? (
          <div className="profile-card">
            <LeaderboardSection />
          </div>
        ) : activeTab === 'friends' ? (
          <div className="profile-card">
            <FriendsSection onJoinRoom={onBack} onOpenAuth={onOpenAuth} />
          </div>
        ) : activeTab === 'settings' ? (
          profile && !state.recoveryToken ? (
            <div className="profile-card">
              {/* Account Settings & Security Section */}
              <AccountSettingsSection profile={profile} />

              {/* Billing & VIP Membership Section */}
              <BillingSection />

              {/* Token Room Formula Hint */}
              <div
                style={{
                  background: 'rgba(0,0,0,0.3)',
                  borderRadius: 12,
                  padding: 14,
                  fontSize: '0.86em',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                <div style={{ fontWeight: 700, color: '#a7f3d0' }}>
                  🍵 {isMn ? 'Цайтай өрөөний доод босго (Тооцоолол):' : 'Token Room Minimum Balance Formula:'}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', opacity: 0.9 }}>
                  <span>{isMn ? '2, 3, эсвэл 4 тоглогч (13 мод × 3):' : '2, 3, or 4 Players (13 cards × 3):'}</span>
                  <strong style={{ color: profile.tsaiBalance >= 39 ? '#4ade80' : '#f87171' }}>
                    39 🍵 {profile.tsaiBalance >= 39 ? '✓' : '✗'}
                  </strong>
                </div>
              </div>

              {/* Actions Footer */}
              <div className="profile-page__footer">
                <button
                  type="button"
                  className="danger"
                  onClick={() => {
                    actions.logout();
                    onBack();
                  }}
                  style={{ padding: '8px 18px', fontWeight: 700 }}
                >
                  {t.signOutAction}
                </button>
                <button
                  type="button"
                  onClick={onBack}
                  style={{ padding: '8px 20px', fontWeight: 700 }}
                >
                  {isMn ? 'Тоглоом руу буцах' : 'Back to Game'}
                </button>
              </div>
            </div>
          ) : (
            <div className="profile-card" style={{ maxWidth: 540, margin: '0 auto', width: '100%' }}>
              {onOpenAuth ? (
                <div style={{ textAlign: 'center', padding: '24px 16px' }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>⚙️</div>
                  <h2 style={{ color: '#fde68a', margin: '0 0 10px', fontSize: '1.4em' }}>
                    {isMn ? 'Бүртгэлээрээ нэвтэрнэ үү' : 'Please Sign In'}
                  </h2>
                  <p style={{ color: '#cbd5e1', fontSize: '0.92em', margin: '0 0 24px', lineHeight: 1.5 }}>
                    {isMn
                      ? 'Тохиргоо болон VIP эрхээ удирдахын тулд бүртгэлээрээ нэвтэрнэ үү.'
                      : 'Sign in to manage your account settings and VIP subscription.'}
                  </p>
                  <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => onOpenAuth('signin')}
                      style={{ background: 'var(--accent-color)', color: '#000', fontWeight: 800, padding: '10px 24px' }}
                    >
                      {t.signInTab}
                    </button>
                    <button
                      type="button"
                      onClick={() => onOpenAuth('signup')}
                      style={{ background: 'rgba(251, 191, 36, 0.25)', color: '#fde68a', border: '1px solid #fbbf24', fontWeight: 800, padding: '10px 24px' }}
                    >
                      {t.signUpTab}
                    </button>
                  </div>
                </div>
              ) : (
                <AuthForms />
              )}
            </div>
          )
        ) : profile && !state.recoveryToken ? (
          /* Signed-in Profile View */
          <div className="profile-card">
            {/* User Identity + Tsai Balance Hero Banner */}
            <div className="profile-hero">
              <div className="profile-hero__identity">
                {(() => {
                  const heroAvatar = getUserAvatar(profile.id, profile.displayName);
                  return (
                    <div
                      className="profile-hero__avatar"
                      style={{
                        background: heroAvatar.gradient,
                        borderColor: heroAvatar.borderColor,
                        boxShadow: `0 0 16px ${heroAvatar.glow}`,
                        color: '#ffffff',
                        fontWeight: 800,
                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
                      }}
                      title={`${profile.displayName} · ${levelInfo.rankTitle}`}
                    >
                      {heroAvatar.initial}
                    </div>
                  );
                })()}
                <div className="profile-hero__info">
                  <div className="profile-hero__name-row">
                    <span className="profile-hero__name">
                      {isPaidTier ? '👑' : '👤'} {profile.displayName}
                    </span>
                    <span
                      style={{
                        fontSize: '0.74em',
                        fontWeight: 800,
                        padding: '2px 8px',
                        borderRadius: 99,
                        background: 'rgba(56, 189, 248, 0.2)',
                        color: '#38bdf8',
                        border: '1px solid rgba(56, 189, 248, 0.4)',
                      }}
                    >
                      Lv.{levelInfo.level}
                    </span>
                    <span
                      style={{
                        fontSize: '0.72em',
                        fontWeight: 800,
                        padding: '2px 9px',
                        borderRadius: 99,
                        background: isPaidTier
                          ? 'linear-gradient(135deg, #f59e0b, #fbbf24)'
                          : 'rgba(255,255,255,0.15)',
                        color: isPaidTier ? '#000' : '#e2e8f0',
                        border: isPaidTier ? 'none' : '1px solid rgba(255,255,255,0.25)',
                      }}
                    >
                      {isPaidTier
                        ? isMn
                          ? '👑 ТӨЛБӨРТЭЙ (PAID VIP)'
                          : '👑 PAID TIER (VIP)'
                        : isMn
                        ? 'ҮНЭГҮЙ ЭРХ (FREE)'
                        : 'FREE TIER'}
                    </span>
                  </div>
                  <div className="profile-hero__meta-row">
                    <span style={{ fontSize: '0.85em', opacity: 0.75 }}>{profile.email}</span>
                    <span
                      style={{
                        fontSize: '0.68em',
                        fontWeight: 800,
                        padding: '1px 7px',
                        borderRadius: 99,
                        background:
                          profile.emailVerified === false
                            ? 'rgba(245, 158, 11, 0.2)'
                            : 'rgba(16, 185, 129, 0.2)',
                        color: profile.emailVerified === false ? '#fde68a' : '#6ee7b7',
                        border:
                          profile.emailVerified === false
                            ? '1px solid rgba(245, 158, 11, 0.45)'
                            : '1px solid rgba(16, 185, 129, 0.45)',
                      }}
                    >
                      {profile.emailVerified === false
                        ? isMn
                          ? '⚠️ Баталгаажаагүй'
                          : '⚠️ Unverified'
                        : isMn
                        ? '✓ Баталгаажсан'
                        : '✓ Verified'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Tsai Balance Box */}
              <div className="profile-hero__tsai-box">
                <div style={{ fontSize: '0.75em', opacity: 0.85, color: '#fde68a', fontWeight: 600 }}>
                  {t.tsaiBalanceLabel}
                </div>
                <div style={{ fontSize: '1.85em', fontWeight: 900, color: '#fbbf24', marginTop: 2 }}>
                  🍵 {profile.tsaiBalance}
                </div>
              </div>
            </div>

            {/* Level & XP Progression Card */}
            <div className="profile-progression-card">
              {/* Level & Rank Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: '1.6em' }}>{levelInfo.rankBadge}</span>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 800, fontSize: '1.15em', color: levelInfo.tierColor }}>
                        {levelInfo.rankTitle}
                      </span>
                      <span
                        style={{
                          fontSize: '0.78em',
                          fontWeight: 800,
                          padding: '2px 8px',
                          borderRadius: 99,
                          background: 'rgba(56, 189, 248, 0.2)',
                          color: '#38bdf8',
                          border: '1px solid rgba(56, 189, 248, 0.4)',
                        }}
                      >
                        Lv.{levelInfo.level}
                      </span>
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: '0.95em', fontWeight: 800, color: '#fde68a' }}>
                    {t.totalXpLabel(levelInfo.totalXp)}
                  </span>
                </div>
              </div>

              {/* Progress Bar */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78em', opacity: 0.85, marginBottom: 6 }}>
                  <span>{isMn ? `Түвшин ${levelInfo.level} ахиц` : `Level ${levelInfo.level} Progress`}</span>
                  <span>{levelInfo.currentLevelXp} / {levelInfo.nextLevelXp} XP ({levelInfo.progressPercent}%)</span>
                </div>
                <div
                  style={{
                    height: 12,
                    width: '100%',
                    background: 'rgba(0, 0, 0, 0.55)',
                    borderRadius: 999,
                    overflow: 'hidden',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                  }}
                  role="progressbar"
                  aria-valuenow={levelInfo.progressPercent}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${levelInfo.progressPercent}%`,
                      background: `linear-gradient(90deg, #38bdf8, ${levelInfo.tierColor})`,
                      borderRadius: 999,
                      transition: 'width 0.4s ease',
                    }}
                  />
                </div>
                <div style={{ fontSize: '0.76em', color: '#94a3b8', marginTop: 5, textAlign: 'right' }}>
                  {t.nextLevelHint(levelInfo.nextLevelXp - levelInfo.currentLevelXp)}
                </div>
              </div>

              {/* Performance Stats Grid: Wins / Games / Win Rate */}
              <div className="profile-stats-grid">
                <div className="profile-stat-box">
                  <div className="profile-stat-box__label">{isMn ? 'Хожил' : 'Wins'}</div>
                  <div className="profile-stat-box__val" style={{ color: '#4ade80' }}>
                    {t.statsWins(profile.wins)}
                  </div>
                </div>
                <div className="profile-stat-box">
                  <div className="profile-stat-box__label">{isMn ? 'Нийт тоглолт' : 'Played'}</div>
                  <div className="profile-stat-box__val" style={{ color: '#f1f5f9' }}>
                    {t.statsGames(profile.gamesPlayed)}
                  </div>
                </div>
                <div className="profile-stat-box">
                  <div className="profile-stat-box__label">{isMn ? 'Хожлын %' : 'Win Rate'}</div>
                  <div className="profile-stat-box__val" style={{ color: '#38bdf8' }}>
                    {t.statsWinRate(String(winRate))}
                  </div>
                </div>
              </div>
            </div>

            {/* Weekly Token Claim Box */}
            <div
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: profile.weeklyClaimAvailable
                  ? '1px solid rgba(251, 191, 36, 0.65)'
                  : '1px solid rgba(255,255,255,0.14)',
                borderRadius: 14,
                padding: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ fontWeight: 800, fontSize: '0.96em', color: '#fde68a' }}>
                  🎁 {isMn ? 'Долоо хоног бүрийн Цай урамшуулал' : 'Weekly Tsai Token Claim'}
                </div>
                <span
                  style={{
                    fontSize: '0.78em',
                    fontWeight: 800,
                    color: isPaidTier ? '#fbbf24' : '#4ade80',
                    background: 'rgba(0,0,0,0.4)',
                    padding: '3px 10px',
                    borderRadius: 99,
                  }}
                >
                  +{weeklyAmount} 🍵 / {isMn ? '7 хоног' : 'week'}
                </span>
              </div>

              <button
                type="button"
                disabled={!profile.weeklyClaimAvailable}
                onClick={actions.claimWeeklyTsai}
                style={{
                  background: profile.weeklyClaimAvailable
                    ? 'linear-gradient(135deg, #f59e0b, #fbbf24)'
                    : 'rgba(255,255,255,0.1)',
                  color: profile.weeklyClaimAvailable ? '#000' : 'rgba(255,255,255,0.55)',
                  fontWeight: 800,
                  padding: '12px 16px',
                  fontSize: '0.98em',
                  borderRadius: 10,
                  border: profile.weeklyClaimAvailable
                    ? 'none'
                    : '1px solid rgba(255,255,255,0.18)',
                  cursor: profile.weeklyClaimAvailable ? 'pointer' : 'not-allowed',
                }}
              >
                {profile.weeklyClaimAvailable
                  ? isMn
                    ? `🎁 +${weeklyAmount} 🍵 Цай урамшуулал авах`
                    : `🎁 Claim +${weeklyAmount} 🍵 Weekly Tokens`
                  : isMn
                  ? `🔒 +${weeklyAmount} 🍵 Цай авах (7 хоногт 1 удаа идэвхжинэ)`
                  : `🔒 Claim +${weeklyAmount} 🍵 Tokens (Active Once a Week)`}
              </button>

              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8em', opacity: 0.8, flexWrap: 'wrap', gap: 6 }}>
                {lastClaimDate && (
                  <span>
                    🕒 {isMn ? `Сүүлд авсан: ${lastClaimDate}` : `Last redeemed: ${lastClaimDate}`}
                  </span>
                )}
                {!profile.weeklyClaimAvailable && nextWeeklyDate && (
                  <span style={{ color: '#fde68a' }}>
                    ⏳ {t.weeklyNextAvailable(nextWeeklyDate)}
                  </span>
                )}
              </div>
            </div>

            {/* Recent Match History Across All Rooms */}
            <div
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 14,
                padding: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 800, fontSize: '0.98em', color: '#fde68a' }}>
                  📜 {isMn ? 'Сүүлийн тоглолтууд (Бүх өрөөний түүх)' : 'Recent Matches (All Rooms)'}
                  {userRecentMatches && userRecentMatches.length > 0 ? ` (${userRecentMatches.length})` : ''}
                </div>
                <a
                  href="/history"
                  onClick={(e) => {
                    e.preventDefault();
                    if (onOpenInfoPage) {
                      onOpenInfoPage('history' as any);
                    } else {
                      window.history.pushState({}, '', '/history');
                      window.dispatchEvent(new PopStateEvent('popstate'));
                    }
                  }}
                  style={{ fontSize: '0.8rem', color: '#38bdf8', textDecoration: 'none' }}
                >
                  {isMn ? 'Өрөөний дэлгэрэнгүй түүх →' : 'View Room History →'}
                </a>
              </div>

              <div className="player-profile-history-list" style={{ maxHeight: 320 }}>
                {state.inspectingPlayerLoading && (!userRecentMatches || userRecentMatches.length === 0) ? (
                  <div className="player-profile-empty-history">
                    {isMn ? 'Түүхийг ачааллаж байна...' : 'Loading match records...'}
                  </div>
                ) : userRecentMatches && userRecentMatches.length > 0 ? (
                  userRecentMatches.map((match) => (
                    <div key={match.id} className="player-profile-match-row">
                      <div className="match-row-left">
                        <span className="match-game-type">{formatGameTypeName(match.gameType, isMn)}</span>
                        <span className="match-player-count">
                          {isMn ? `${match.playerCount} тоглогч` : `${match.playerCount} players`}
                        </span>
                      </div>

                      <div className="match-row-right">
                        <span className={`match-badge${match.won ? ' is-win' : ' is-loss'}`}>
                          {match.won ? (isMn ? '🏆 Хожсон' : '🏆 Won') : (isMn ? 'Хожигдсон' : 'Defeat')}
                        </span>
                        <span className="match-time">{formatRelativeTime(match.finishedAt, isMn)}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="player-profile-empty-history">
                    <span>🎲</span>
                    <p>
                      {isMn
                        ? 'Одоогоор нийтийн тоглолтын түүх бүртгэгдээгүй байна.'
                        : 'No public match history recorded yet.'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Quick navigation to settings */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: 'rgba(255, 255, 255, 0.04)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: 12,
                padding: '12px 16px',
                flexWrap: 'wrap',
                gap: 8,
              }}
            >
              <span style={{ fontSize: '0.88em', color: '#cbd5e1' }}>
                ⚙️ {isMn ? 'Нууц үг, имэйл солих эсвэл VIP гишүүнчлэл удирдах уу?' : 'Need to change password, email, or manage VIP subscription?'}
              </span>
              <button
                type="button"
                onClick={() => handleSelectTab('settings')}
                style={{
                  background: 'rgba(56, 189, 248, 0.15)',
                  border: '1px solid rgba(56, 189, 248, 0.4)',
                  color: '#38bdf8',
                  fontWeight: 700,
                  padding: '6px 14px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: '0.85em',
                }}
              >
                {isMn ? 'Тохиргоо руу очих →' : 'Go to Settings →'}
              </button>
            </div>

            {/* Actions Footer */}
            <div className="profile-page__footer">
              <button
                type="button"
                onClick={onBack}
                style={{ padding: '8px 20px', fontWeight: 700, marginLeft: 'auto' }}
              >
                {isMn ? 'Тоглоом руу буцах' : 'Back to Game'}
              </button>
            </div>
          </div>
        ) : (
          /* Signed-out Register / Sign In / Verify Email / Forgot & Reset Password View */
          <div className="profile-card" style={{ maxWidth: 540, margin: '0 auto', width: '100%' }}>
            {onOpenAuth ? (
              <div style={{ textAlign: 'center', padding: '24px 16px' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>👤</div>
                <h2 style={{ color: '#fde68a', margin: '0 0 10px', fontSize: '1.4em' }}>
                  {isMn ? 'Бүртгэлээрээ нэвтэрнэ үү' : 'Please Sign In'}
                </h2>
                <p style={{ color: '#cbd5e1', fontSize: '0.92em', margin: '0 0 24px', lineHeight: 1.5 }}>
                  {isMn
                    ? 'Өөрийн тоглогчийн профайл, XP түвшин, хожлын статистик болон Цай үлдэгдлийг харахын тулд нэвтэрнэ үү.'
                    : 'Sign in to view your player profile, level progression, game stats, and Tsai token rewards.'}
                </p>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => onOpenAuth('signin')}
                    style={{ background: 'var(--accent-color)', color: '#000', fontWeight: 800, padding: '10px 24px' }}
                  >
                    {t.signInTab}
                  </button>
                  <button
                    type="button"
                    onClick={() => onOpenAuth('signup')}
                    style={{ background: 'rgba(251, 191, 36, 0.25)', color: '#fde68a', border: '1px solid #fbbf24', fontWeight: 800, padding: '10px 24px' }}
                  >
                    {t.signUpTab}
                  </button>
                </div>
              </div>
            ) : (
              <AuthForms />
            )}
          </div>
        )}

        {/* Informational Footer Links */}
        {onOpenInfoPage && (
          <div className="profile-links">
            <a
              href="/subscription"
              onClick={(e) => {
                e.preventDefault();
                onOpenInfoPage('subscription');
              }}
              style={{ color: '#fde68a', fontWeight: 700 }}
            >
              👑 {isMn ? 'VIP Гишүүнчлэлийн мэдээлэл' : 'VIP Benefits & Subscription'}
            </a>
            <a
              href="/terms"
              onClick={(e) => {
                e.preventDefault();
                onOpenInfoPage('terms');
              }}
            >
              {isMn ? 'Үйлчилгээний нөхцөл' : 'Terms of Service'}
            </a>
            <a
              href="/privacy"
              onClick={(e) => {
                e.preventDefault();
                onOpenInfoPage('privacy');
              }}
            >
              {isMn ? 'Нууцлалын бодлого' : 'Privacy Policy'}
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
